import audioIcon from 'Assets/icons/audio.svg';
import hiperlinkIcon from 'Assets/icons/hiperlink.svg';
import powerPointIcon from 'Assets/images/power-point.svg';
import zipIcon from 'Assets/images/zip.svg';
import { isSameYear, format } from 'date-fns';
import type { ISharedItemCreatedNotification } from 'Interfaces/notifications';
import { mapIcon } from 'Models/FileType';
import type { IMessageDocument } from 'Models/MessageModel';
import type { SharedItem, SharedItemAuthor } from 'Modules/sharedItems/index.types';

// Whether the item's content type is an image.
export const isSharedItemImage = (item: SharedItem): boolean =>
  !!item.contentType?.startsWith('image/');

// Whether the content type is audio.
const isSharedItemAudio = (contentType?: string): boolean =>
  !!contentType?.startsWith('audio/');

// Whether the content type matches any known zip MIME variant.
const isSharedItemZip = (contentType?: string): boolean =>
  !!contentType &&
  [
    'application/zip',
    'application/x-zip',
    'application/x-zip-compressed',
    'application/zip-compressed',
  ].includes(contentType);

// Whether the content type matches any known PowerPoint MIME variant (legacy or OOXML).
const isSharedItemPowerPoint = (contentType?: string): boolean =>
  !!contentType &&
  [
    'application/mspowerpoint',
    'application/powerpoint',
    'application/x-mspowerpoint',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ].includes(contentType);

/** Icon for a shared item: either "render the item's own image" or "use this icon src". */
export type SharedItemIcon =
  | { kind: 'image' }
  | { kind: 'src'; src: string };

/**
 * Resolves the icon for a shared item.
 * Reuses {@link mapIcon} for known file MIME types, with dedicated icons for
 * links, images, audio, zip, and PowerPoint.
 */
export const getSharedItemIcon = (item: SharedItem): SharedItemIcon => {
  if (item.itemType === 'link') {
    return { kind: 'src', src: hiperlinkIcon };
  }

  if (isSharedItemImage(item)) {
    return { kind: 'image' };
  }

  if (isSharedItemAudio(item.contentType)) {
    return { kind: 'src', src: audioIcon };
  }

  if (isSharedItemZip(item.contentType)) {
    return { kind: 'src', src: zipIcon };
  }

  if (isSharedItemPowerPoint(item.contentType)) {
    return { kind: 'src', src: powerPointIcon };
  }

  return { kind: 'src', src: mapIcon({} as File, item.contentType) };
};

/**
 * Maps a `SharedItemCreated` Pusher payload into the {@link SharedItem} list shape.
 * `messageId` on the event is the ancestor message id (backend contract).
 */
export const mapSharedItemCreatedToSharedItem = (
  payload: ISharedItemCreatedNotification
): SharedItem => {
  const author: SharedItemAuthor = {};

  if (payload.sharedByPersonId > 0) {
    author.personId = payload.sharedByPersonId;
  }

  if (payload.sharedByPhone) {
    author.phone = payload.sharedByPhone;
  }

  return {
    id: payload.itemId,
    conversationId: payload.conversationId,
    itemType: payload.itemType,
    ancestorId: payload.messageId,
    ...(payload.fileInfoId ? { fileInfoId: payload.fileInfoId } : {}),
    itemDisplayName: payload.itemDisplayName,
    ...(payload.targetUrl ? { targetUrl: payload.targetUrl } : {}),
    ...(payload.contentType ? { contentType: payload.contentType } : {}),
    author,
    sharedAt: payload.shared ?? new Date(0).toISOString(),
    isUnavailable: false,
    isDeleted: false,
  };
};

/**
 * Adapts a {@link SharedItem} into the {@link IMessageDocument} shape,
 * so it can be reused by existing file/link preview components.
 */
export const mapSharedItemToMessageDocument = (
  item: SharedItem
): IMessageDocument => ({
  externalId: item.fileInfoId || item.id,
  contentType: item.contentType,
  description: item.itemDisplayName,
  byteCount: item.byteCount,
  created: item.sharedAt,
  isDeleted: item.isDeleted,
  url: item.targetUrl || '',
  name: item.itemDisplayName,
  previewUrl: item.targetUrl,
});

/**
 * Formats a date for display, e.g. "Jan 5" or "Jan 5, 2024".
 * The year is omitted when the date falls in the current year.
 */
export const formatSharedItemDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();

  return isSameYear(date, now) ? format(date, 'MMM d') : format(date, 'MMM d, yyyy');
};